import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { ApplicationValidationHelper } from '../../../../shared/helpers/application-validator/application-validator.helper';
import { StepLink } from '../../../../shared/models/step-link';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { FormEntryService } from '../../../../../../core/services/form-entry/form-entry.service';
import { ValidationResult } from '../../../../../../core/services/validation/models/validation-result';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { ScrollHelper } from '../../../../../../core/helpers/scroll/scroll-helper';
import { FocusHelper } from '../../../../../../core/helpers/focus/focus-helper';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';

@Component({
    selector: 'app-application-coordination-base',
    changeDetection: ChangeDetectionStrategy.Eager,
    template:
        '<!-- This component serves as a base and does not render any content -->',
})
export abstract class ApplicationCoordinationBaseComponent<
        TRequest,
        TResponse,
        TFormModel,
    >
    implements OnInit, OnDestroy
{
    protected destroy$ = new Subject<void>();
    private readonly hostElementRef = inject(ElementRef<HTMLElement>);

    abstract form:
        | FormGroup<FormGroupType<TFormModel>>
        | FormGroup<NestedFormGroupType<TFormModel>>;

    applicationState: ApplicationState | null =
        this.applicationStateService.getCurrentApplicationState();
    validationResult: ValidationResult | null = null;
    hasChanges: boolean = false;
    applicationId: string | undefined;

    constructor(
        protected readonly route: ActivatedRoute,
        protected readonly stepService: StepNavigationService,
        protected readonly router: Router,
        protected readonly formEntryService: FormEntryService,
        protected readonly applicationStateService: ApplicationStateService,
        protected readonly applicationFormControlManagerService: ApplicationFormControlManagerService,
        protected readonly returnRouteService: ReturnRouteService,
    ) {}

    ngOnInit(): void {
        ScrollHelper.scrollToTopOfPage();

        this.loadForm()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setupReadOnly();

                this.formEntryService.handleFormDirtying<TFormModel>(
                    this.form,
                    this.destroy$,
                    (newValue: boolean) => {
                        // Don't track changes if the form is read-only
                        if (!this.isReadOnly()) {
                            this.hasChanges = newValue;
                        }
                    },
                );

                this.handleNextClick();
                this.handlePreviousClick();
                this.handleSaveAndExitClick();
                this.handleSubmitForCoordinationClick();
                this.handleHandoverToEwaClick();
                this.handleSubmitToFccClick();
                this.postFormBindingActions();
                this.focusFirstField();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected loadForm(): Observable<void> {
        return this.route.paramMap.pipe(
            takeUntil(this.destroy$),
            switchMap((params: ParamMap) => {
                this.applicationId = params.get('id')!;
                return this.getFormDataFromApi(this.applicationId);
            }),
            tap((response: TResponse) => {
                this.onLoad(response);
            }),
            map(() => void 0),
        );
    }

    protected handleNextClick(): void {
        this.handleAction(
            this.stepService.nextClick$,
            (result: ValidationResult | void) => {
                if (result) {
                    if (this.shouldNavigate(result)) {
                        this.stepService.goToNextStep();
                    } else {
                        ApplicationValidationHelper.markAllRequiredAsTouched(
                            this.form,
                        );

                        this.validationResult = result;
                    }
                } else {
                    this.stepService.goToNextStep();
                }
            },
        );
    }

    protected handlePreviousClick(): void {
        this.handleAction(
            this.stepService.previousClick$,
            (result: ValidationResult | void) => {
                if (result) {
                    if (this.shouldNavigate(result)) {
                        this.stepService.goToPreviousStep();
                    }
                } else {
                    this.stepService.goToPreviousStep();
                }
            },
        );
    }

    protected handleSaveAndExitClick(): void {
        this.handleAction(this.stepService.saveAndClose$, () => {
            const returnRoute = this.returnRouteService.consume();
            if (returnRoute) {
                this.router.navigate([returnRoute]);
            } else {
                this.router.navigate([
                    RouteConstants.Dashboard.getFullRoute('INDEX'),
                ]);
            }
        });
    }

    protected handleSubmitForCoordinationClick(): void {
        this.handleAction(this.stepService.submitForCoordinationClick$, () => {
            this.submitForCoordinationAction();
        });
    }

    protected handleHandoverToEwaClick(): void {
        this.handleAction(this.stepService.handoverToEwa$, () => {
            this.handoverToEwaAction();
        });
    }

    protected handleSubmitToFccClick(): void {
        this.handleAction(this.stepService.submitToFccClick$, () => {
            this.submitToFccAction();
        });
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle any extra actions that are specific to the page that
     * need to occur AFTER the form is initially loaded.
     */
    protected postFormBindingActions(): void {}

    /**
     * Moves keyboard focus to the first field on the page after it loads. This ensures that
     * regardless of how the user navigated to the page (Next, Previous, or the Step Menu), their
     * focus starts on the first field.
     */
    protected focusFirstField(): void {
        if (!this.shouldAutoFocusFirstField()) {
            return;
        }

        FocusHelper.deferFocusFirstField(this.hostElementRef.nativeElement);
    }

    /**
     * (OPTIONAL) Override in a child class to opt out of automatically focusing the first field on load.
     * This is useful for pages that manage their own focus.
     */
    protected shouldAutoFocusFirstField(): boolean {
        return true;
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle submit for coordination action, if applicable.
     */
    protected submitForCoordinationAction(): void {}

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle submit to EWA action, if applicable.
     */
    protected handoverToEwaAction(): void {}

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle submit to FCC action, if applicable.
     */
    protected submitToFccAction(): void {}

    /**
     * Determines if the application is in read-only mode.
     *
     * @return true if the application is read-only; otherwise, false.
     */
    protected isReadOnly(): boolean {
        return this.applicationFormControlManagerService.isReadOnly();
    }

    /**
     * Initializes and enforces read-only behavior for the current application context.
     *
     * When the current application is marked as read-only:
     * - Disables the root FormGroup (cascading to all child controls) without emitting value change events.
     * - Resets hasChanges to prevent save operations, navigation guards, or step transitions from triggering.
     *
     * This method should be invoked once during initialization since the read-only state
     * does not change during the lifetime of the page.
     */
    private setupReadOnly(): void {
        if (this.applicationFormControlManagerService.isReadOnly()) {
            this.form.disable({ emitEvent: false });
            this.hasChanges = false;
        }
    }

    private shouldNavigate(result: ValidationResult): boolean {
        return this.form.valid && result.isValid;
    }

    private handleAction<T extends StepLink | void>(
        action$: Observable<T>,
        onSuccess: (value: ValidationResult | void) => void,
    ): void {
        action$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((_: T) => {
                    if (this.isReadOnly()) {
                        return of(undefined as void);
                    }

                    const request = this.generateRequestObject();
                    return this.saveFormDataToApi(request).pipe(
                        tap(() => {
                            // Setting the flag to "false" allows the page navigation to proceed and not be blocked by the unsaved changes.
                            // See: BlockNavigationIfChangeGuard logic applied on all application pages.
                            this.hasChanges = false;
                        }),
                        map((response: ValidationResult) => response),
                    );
                }),
            )
            .subscribe({
                next: (value) => {
                    onSuccess(value);
                },
            });
    }

    private onLoad(response: TResponse): void {
        this.bindForm(response);
    }

    /**
     * Logic to bind the response data to the page's form.
     * @param response
     */
    protected abstract bindForm(response: TResponse): void;

    /**
     * API call to get the form data for the page.
     * @param applicationId The application's Id.
     */
    protected abstract getFormDataFromApi(
        applicationId: string,
    ): Observable<TResponse>;

    /**
     * API call to save the form data to the server.
     * @param request
     */
    protected abstract saveFormDataToApi(
        request: TRequest,
    ): Observable<ValidationResult>;

    /**
     * Generates the request object (from the form) in preparate to send the data to the server during saving actions.
     */
    protected abstract generateRequestObject(): TRequest;
}
